import { ColDef, ValueFormatterParams } from 'ag-grid-community'
import { NestedLegItem, NegotiationFieldMeta, AmountFormat } from 'api'
import { Locale, Translation } from 'locale'
import { stopPastePropagation } from 'utils'
import * as constants from 'view/styles/constants'
import { ValidationCellRenderer, TypedContext, TradingEntityPublishedNameRenderer } from 'view/components/datagrid'
import { NumericInput, TextInput } from 'view/components/inputs'
import {
  booleanFormatter,
  getNotionalFormatOptions,
  getStartConsiderationFormatOptions,
  haircutTypeFormatter,
  makeHaircutFormatter,
  makeNumberFormatter,
  makeRateFormatter
} from '../datagrid'
import { BooleanSelectbox } from '../entity'
import { makeValueGetter } from './dataGetters'
import { PreAssignmentSubGridColumnId } from './PreAssignmentColumnNames'
import { PreAssignmentNames } from './PreAssignmentNames'

export const removeEditParams = ({ cellRendererParams, editable, ...rest }: ColDef) => {
  return rest
}

export const makePreAssignmentNotionalColumn = (overrides?: Partial<ColDef<NestedLegItem>>) => ({
  colId: PreAssignmentSubGridColumnId.NOTIONAL,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: { validationField: PreAssignmentNames.NOTIONAL_VALIDATION },
  equals: () => false,
  headerName: Locale.translate(Translation.TC_NOTIONAL_SHORT),
  type: 'numericColumn',
  valueFormatter: (params: ValueFormatterParams<NestedLegItem>) => {
    const formatOptions = getNotionalFormatOptions(params.context.userPreference.amountFormat, false, false, false)
    return Locale.formatNumber(params.value, formatOptions)
  },
  valueGetter: makeValueGetter(NegotiationFieldMeta.NOTIONAL),
  width: 100,
  cellEditorParams: {
    inputComponent: NumericInput,
    inputParams: (_leg: NestedLegItem, context: TypedContext<NestedLegItem>) => ({
      dataTestId: 'PreAssignmentSubGridColumnId-NotionalNumericInput',
      formatOptions: getNotionalFormatOptions(context.userPreference.amountFormat),
      isInteger: context.userPreference.amountFormat === AmountFormat.ACTUAL,
      onPaste: stopPastePropagation
    }),
    defaultField: PreAssignmentNames.NOTIONAL_NEGOTIATED,
    isModifiedField: PreAssignmentNames.NOTIONAL_IS_MODIFIED,
    updateField: PreAssignmentNames.NOTIONAL_MODIFIED,
    validationField: PreAssignmentNames.NOTIONAL_VALIDATION
  },
  ...overrides
})

export const makePreAssignmentMyEntityColumn = (overrides?: Partial<ColDef<NestedLegItem>>) => ({
  colId: PreAssignmentSubGridColumnId.MY_ENTITY,
  cellRenderer: TradingEntityPublishedNameRenderer,
  cellRendererParams: { validationField: PreAssignmentNames.FUND_ID_VALIDATION },
  equals: () => false,
  headerName: Locale.translate(Translation.TC_MY_ENTITY),
  valueGetter: makeValueGetter(NegotiationFieldMeta.FUND_ID),
  width: 150,
  ...overrides
})

export const makePreAssignmentStartConsiderationColumn = (overrides?: Partial<ColDef<NestedLegItem>>) => ({
  colId: PreAssignmentSubGridColumnId.START_CONSIDERATION,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: PreAssignmentNames.START_CONSIDERATION_VALIDATION,
  equals: () => false,
  headerName: Locale.translate(Translation.TC_START_CONSIDERATION),
  type: 'numericColumn',
  valueFormatter: makeNumberFormatter({ minDp: 2 }),
  valueGetter: makeValueGetter(NegotiationFieldMeta.START_CONSIDERATION),
  width: 150,
  cellEditorParams: {
    inputComponent: NumericInput,
    inputParams: () => ({
      formatOptions: getStartConsiderationFormatOptions(),
      onPaste: stopPastePropagation,
      validationField: PreAssignmentNames.START_CONSIDERATION_VALIDATION
    }),
    defaultField: PreAssignmentNames.START_CONSIDERATION_NEGOTIATED,
    isModifiedField: PreAssignmentNames.START_CONSIDERATION_IS_MODIFIED,
    updateField: PreAssignmentNames.START_CONSIDERATION_MODIFIED,
    validationField: PreAssignmentNames.START_CONSIDERATION_VALIDATION
  },
  ...overrides
})

export const makePreAssignmentStartConsiderationOverrideColumn = (overrides?: Partial<ColDef<NestedLegItem>>) => ({
  colId: PreAssignmentSubGridColumnId.START_CONSIDERATION_OVERRIDE,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: { validationField: PreAssignmentNames.START_CONSIDERATION_OVERRIDE_VALIDATION },
  equals: () => false,
  headerName: Locale.translate(Translation.TC_START_CONSIDERATION_OVERRIDE),
  valueFormatter: booleanFormatter,
  valueGetter: makeValueGetter(NegotiationFieldMeta.START_CONSIDERATION_OVERRIDE),
  width: 100,
  cellEditorParams: {
    inputComponent: BooleanSelectbox,
    inputParams: () => ({
      withPortal: true,
      onPaste: stopPastePropagation,
      validationField: PreAssignmentNames.START_CONSIDERATION_OVERRIDE_VALIDATION
    }),
    defaultField: PreAssignmentNames.START_CONSIDERATION_OVERRIDE_NEGOTIATED,
    isModifiedField: PreAssignmentNames.START_CONSIDERATION_OVERRIDE_IS_MODIFIED,
    updateField: PreAssignmentNames.START_CONSIDERATION_OVERRIDE_MODIFIED,
    validationField: PreAssignmentNames.START_CONSIDERATION_OVERRIDE_VALIDATION
  },
  ...overrides
})

export const makePreAssignmentRateColumn = (overrides?: Partial<ColDef<NestedLegItem>>) => ({
  colId: PreAssignmentSubGridColumnId.RATE,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: { validationField: PreAssignmentNames.RATE_VALIDATION },
  headerName: Locale.translate(Translation.TC_RATE),
  type: 'numericColumn',
  valueFormatter: makeRateFormatter(),
  valueGetter: makeValueGetter(NegotiationFieldMeta.RATE),
  width: 70,
  ...overrides
})

export const makePreAssignmentAccruedInterestColumn = (overrides?: Partial<ColDef<NestedLegItem>>) => ({
  colId: PreAssignmentSubGridColumnId.ACCRUED_INTEREST,
  cellRenderer: ValidationCellRenderer,
  headerName: Locale.translate(Translation.TC_COLLATERAL_ACCRUED_INTEREST),
  type: 'numericColumn',
  valueFormatter: makeNumberFormatter({ minDp: 2 }),
  valueGetter: makeValueGetter(NegotiationFieldMeta.ACCRUED_INTEREST),
  ...overrides
})

export const makePreAssignmentInitiatorBookColumn = (overrides?: Partial<ColDef<NestedLegItem>>) => ({
  colId: PreAssignmentSubGridColumnId.INITIATOR_BOOK,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: { validationField: PreAssignmentNames.INITIATOR_BOOK_VALIDATION },
  equals: () => false,
  headerName: Locale.translate(Translation.TC_BOOK),
  valueGetter: makeValueGetter(NegotiationFieldMeta.INITIATOR_BOOK),
  width: 150,
  cellEditorParams: {
    inputComponent: TextInput,
    inputParams: () => ({
      onPaste: stopPastePropagation,
      maxLength: constants.internalBookingMaxFieldLength,
      validationField: PreAssignmentNames.INITIATOR_BOOK_VALIDATION
    }),
    defaultField: PreAssignmentNames.INITIATOR_BOOK_NEGOTIATED,
    isModifiedField: PreAssignmentNames.INITIATOR_BOOK_IS_MODIFIED,
    updateField: PreAssignmentNames.INITIATOR_BOOK_MODIFIED,
    validationField: PreAssignmentNames.INITIATOR_BOOK_VALIDATION
  },
  ...overrides
})

export const makePreAssignmentInitiatorStrategyColumn = (overrides?: Partial<ColDef<NestedLegItem>>) => ({
  colId: PreAssignmentSubGridColumnId.INITIATOR_STRATEGY,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: { validationField: PreAssignmentNames.INITIATOR_STRATEGY_VALIDATION },
  equals: () => false,
  headerName: Locale.translate(Translation.TC_STRATEGY),
  valueGetter: makeValueGetter(NegotiationFieldMeta.INITIATOR_STRATEGY),
  width: 150,
  cellEditorParams: {
    inputComponent: TextInput,
    inputParams: () => ({
      onPaste: stopPastePropagation,
      maxLength: constants.internalBookingMaxFieldLength,
      validationField: PreAssignmentNames.INITIATOR_STRATEGY_VALIDATION
    }),
    defaultField: PreAssignmentNames.INITIATOR_STRATEGY_NEGOTIATED,
    isModifiedField: PreAssignmentNames.INITIATOR_STRATEGY_IS_MODIFIED,
    updateField: PreAssignmentNames.INITIATOR_STRATEGY_MODIFIED,
    validationField: PreAssignmentNames.INITIATOR_STRATEGY_VALIDATION
  },
  ...overrides
})

export const makePreAssignmentHaircutColumn = (overrides?: Partial<ColDef<NestedLegItem>>) => ({
  colId: PreAssignmentSubGridColumnId.HAIRCUT,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: { validationField: PreAssignmentNames.HAIRCUT_VALIDATION },
  headerName: Locale.translate(Translation.TC_HAIRCUT),
  width: 80,
  valueFormatter: makeHaircutFormatter(),
  ...overrides
})

export const makePreAssignmentHaircutTypeColumn = (overrides?: Partial<ColDef<NestedLegItem>>) => ({
  colId: PreAssignmentSubGridColumnId.HAIRCUT_TYPE,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: { validationField: PreAssignmentNames.HAIRCUT_TYPE_VALIDATION },
  headerName: Locale.translate(Translation.TC_HAIRCUT_TYPE),
  width: 150,
  valueFormatter: haircutTypeFormatter,
  ...overrides
})

export const makePreAssignmentRepoInterestColumn = (overrides?: Partial<ColDef<NestedLegItem>>) => ({
  colId: PreAssignmentSubGridColumnId.REPO_INTEREST,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: { validationField: PreAssignmentNames.REPO_INTEREST_VALIDATION },
  headerName: Locale.translate(Translation.TC_REPO_INTEREST),
  valueGetter: makeValueGetter(NegotiationFieldMeta.REPO_INTEREST),
  valueFormatter: makeNumberFormatter({ minDp: 2 }, true),
  type: 'numericColumn',
  width: 80,
  ...overrides
})

export const makePreAssignmentAllInPriceColumn = (overrides?: Partial<ColDef<NestedLegItem>>) => ({
  colId: PreAssignmentSubGridColumnId.ALL_IN_PRICE,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: { validationField: PreAssignmentNames.ALL_IN_PRICE_VALIDATION },
  headerName: Locale.translate(Translation.TC_ALL_IN_PRICE),
  type: 'numericColumn',
  valueGetter: makeValueGetter(NegotiationFieldMeta.ALL_IN_PRICE),
  valueFormatter: makeNumberFormatter(),
  width: 120,
  ...overrides
})

export const makePreAssignmentEndConsiderationColumn = (overrides?: Partial<ColDef<NestedLegItem>>) => ({
  colId: PreAssignmentSubGridColumnId.END_CONSIDERATION,
  cellRenderer: ValidationCellRenderer,
  cellRendererParams: { validationField: PreAssignmentNames.END_CONSIDERATION_VALIDATION },
  headerName: Locale.translate(Translation.TC_END_CONSIDERATION),
  type: 'numericColumn',
  valueGetter: makeValueGetter(NegotiationFieldMeta.END_CONSIDERATION),
  valueFormatter: makeNumberFormatter({ minDp: 2 }),
  width: 120,
  ...overrides
})
